import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

export interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Right-side panel used for create/edit forms. "Right" is the inline end, so in RTL it slides in
 * from the left without any extra code.
 */
export const Sheet = ({ isOpen, onClose, title, description, children, footer, className }: SheetProps) => {
  const { t } = useI18nTranslation();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  // Portalled to <body>: inside a `space-y-*` page wrapper the fixed overlay would otherwise inherit a top margin.
  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      <button
        type="button"
        aria-label={t('actions.close')}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-foreground/30"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn('relative ms-auto flex h-full w-full max-w-md flex-col border-s border-border bg-card shadow-xl', className)}
      >
        <header className="flex items-start gap-4 border-b border-border p-6">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold text-card-foreground">{title}</h2>
            {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>

        {footer ? <footer className="flex justify-end gap-2 border-t border-border p-6">{footer}</footer> : null}
      </aside>
    </div>,
    document.body,
  );
};
